<script>
  import {getContext, onMount} from 'svelte';
  import {parse, stringify} from 'query-string';
  import {PATTERNS} from '@/const/patterns';
  import {LINKS} from '@/const/link';
  import {PATHS} from '@/const/paths';
  import {LANG_MAP} from '@/map';
  import {mc} from '@/core';
  import {drawerUtil} from '@/helper/drawer';
  import {envUtil} from '@/helper/env';
  import {numUtil} from '@/helper/num';
  import {navAfterRegisterSuccess} from '@/helper/login';
  import {getLocationSearch} from '@/helper/page';
  import {getRedirectTarget, setRedirectTarget, clearRedirectTarget, isValidRedirectTarget} from '@/modules/header/util';
  import {doCaptcha} from '@/captcha';
  import {ga} from '@/da/ga';
  import {sd} from '@/da/sd';
  import {pixelTrackRegister} from '@/da/pixel';
  import {userApi} from '@/api/user';
  import {transferApi} from '@/api/cross-site-transfer';
  import {fixLink$, lang$} from '@/store/lang';
  import {referral$} from '@/store/referral';
  import {createKolState$} from '@/modules/register-vt1/streams';
  import Button from '@/widgets/Button.svelte';
  import EmailInput from '@/widgets/EmailInput.svelte';
  import PasswordInput from '@/widgets/PasswordInput.svelte';
  import {validateEmail} from '@/modules/components/register-form/validateUtil';
  import ConnectAccountTips from '@/modules/components/connect-account-tips/ConnectAccountTips.svelte';
  import ConnectAccountIndicator from '@/modules/components/ConnectAccountIndicator.svelte';
  import RegisterSuccess from './RegisterSuccess.svelte';
  import {totalBonus$} from '../streams';
  import {kolInfo$} from '../componets/kol-exclusive/stream';
  import {IMAGES} from '@/modules/register-vt1/images';
  import SplitLine from '@/modules/login/componets/SplitLine.svelte';
  import ThirdPartyLogin from '@/modules/login/componets/ThirdPartyLogin.svelte';
  import registerLang from '@/modules/components/register-form/RegisterForm.lang';
  import lang from '../RegisterPage.lang';
  import {openRestrictedIP} from '@/modules/register/restricted-ip';

  export let registerUIVersion = 0;
  export let modalUI = false;
  export let accountProps = '';
  export let hideBonusImg = false;

  const LG = getContext('LG');

  const optionalArrowIcon = 'https://img.phemex.com/v1/e325b902-a9bf-430d-a8b0-cbc7aeaa38df/short.svg';
  const terms = LG(registerLang.BySigningUp, {
    Term: `<a class="T1 fw3 noa" href="${$fixLink$(LINKS.PHEMEX_REFERENCES_TERMS_OF_USE, 'CMS')}" target="_blank" rel="noopener noreferrer nofollow">${LG(registerLang.Terms)}</a>`,
    Privacy: `<a class="T1 fw3 noa" href="${$fixLink$(LINKS.PHEMEX_REFERENCES_PRIVACY_POLICY, 'CMS')}" target="_blank" rel="noopener noreferrer nofollow">${LG(registerLang.PrivacyPolicy)}</a>`,
  });

  const ConnectSourceSite = {
    Turkey: 'TURKEY',
  };
  const ChannelEnum = {
    DEFAULT: 'organic traffic',
    C2C: 'C2C',
    KOL: 'KOL',
  };

  let innerWidth = 1200;
  let email = null;
  let password = null;
  let errorEmailMsg = '';
  let errorPasswordMsg = '';
  let isRegistering = false;
  let isInitializing = true;
  let hasReferral = false;
  let referralCode = '';
  let referralGroup = 0;
  let query = {target: '', action: 'Welcome'};
  let isSeeCode = false;
  let isTerm = true;
  let isPasswordTips = false;
  let isShowPasswordTips = false;
  let loginUrl = '';
  let sourceKycUserCode;
  let sourceNoKycUserCode;
  let sourceSite;
  let isConnectDone = false;
  let referralType = 0;
  let channel = ChannelEnum.DEFAULT;
  let modal = 0;

  $: emailAccount = email && email.trim();
  $: errorPasswordMsg = validatePassword(password);
  $: updateSeeCode(referralCode);
  $: updateLoginUrl($fixLink$, query.target, query.modal);

  onMount(() => {
    isInitializing = false;
    const targetStr = getRedirectTarget() || '';
    query = {target: '', action: 'Welcome', ...getLocationSearch()};
    let {referralCode, group, account, code, sourceSite: _sourceSite, connectIdentifier: _connectIdentifier} = query;
    try {
      if (targetStr) {
        query.target = targetStr;
        const targetParams = parse(targetStr.split('?')[1]);
        referralCode = !referralCode && targetParams.referralCode ? targetParams.referralCode : referralCode;
        group = !group && targetParams.group ? targetParams.group : group;
      }
    } catch (error) {}
    email = account || accountProps;
    sourceNoKycUserCode = code && code !== '0' ? code : null;
    sourceKycUserCode = _connectIdentifier;
    sourceSite = _sourceSite;
    referralCode && referral$.set({code: referralCode, group});
    group && numUtil.isNumber(group) && userApi.addGroupClickCnt(group);
  });

  onMount(() =>
    referral$.subscribe(({code, group}) => {
      hasReferral = !!code;
      referralCode = code;
      if (group && numUtil.isNumber(group)) {
        referralGroup = Number(group);
      }
    })
  );

  onMount(() => {
    const params = getLocationSearch();
    modal = params.modal;
    if (modal == 1) {
      modalUI = true;
    }
  });

  onMount(() => {
    if (!referralCode) {
      reportRegisterChannel();
      return;
    }
    createKolState$(referralCode).subscribe((kolState) => {
      if (!kolState.success) {
        return;
      }
      referralType = kolState.data.referralType || 0;
      reportRegisterChannel();
    });
  });

  function reportRegisterChannel() {
    if (!referralType) {
      sd.track('register_landingpage', {referral_code: referralCode, channel});
      return;
    }
    if ([1, 3].includes(referralType)) {
      channel = ChannelEnum.C2C;
    }
    if (referralType === 5) {
      channel = ChannelEnum.KOL;
    }
    sd.track('register_landingpage', {referral_code: referralCode, channel});
  }

  function updateSeeCode(referralCode) {
    if (referralCode) {
      isSeeCode = true;
    }
  }

  function updateLoginUrl(fixLink, target, modal) {
    let res = '';
    if (modal) {
      res += stringify({modal});
    }

    loginUrl = fixLink(PATHS.LOGIN.pathname) + (res ? `?${res}` : '');
  }

  function handleSeeCodeClick() {
    isSeeCode = !isSeeCode;
  }

  async function handleButtonContinueClick() {
    const result = await openRestrictedIP({referralCode, LG, entrance: 'Register', type: 'email', email: emailAccount});
    if (!result) {
      return;
    }
    if (referralCode) {
      sd.track('referral_register', {referral_code_type: query.referralCode ? 'link_referralcode' : 'hand_fill_referralcode'});
    }
    sd.track('web_click', {$screen_name: 'register', $element_content: 'create_account'});
    sd.track('launch_register', {referral_code: referralCode, ui_position: 'register_page'});
    password = password || '';
    isPasswordTips = true;
    errorEmailMsg = validateEmail(emailAccount, LG, lang);
    errorPasswordMsg = validatePassword(password);
    if (emailAccount && password && errorEmailMsg === null && errorPasswordMsg === null) {
      isRegistering = true;
      const captchaResult = await doCaptcha('register');
      if (!captchaResult) {
        mc.send('toast', 'error', {message: LG(lang.NetworkError)});
        isRegistering = false;
        return;
      }
      const params = {
        email: emailAccount,
        password,
        referralCode,
        group: !!referralGroup ? Number(referralGroup) : 0,
        nickName: '',
        lang: LANG_MAP[$lang$] || $lang$,
        userDeviceLang: (navigator.language || navigator.userLanguage).substring(0, 2),
      };
      if (sourceNoKycUserCode) {
        await quickRegister(params, captchaResult);
        return;
      }
      if (sourceKycUserCode) {
        await connectRegister(params, captchaResult);
        return;
      }
      await normalRegister(params, captchaResult);
    }
  }

  async function normalRegister(params, captchaResult) {
    const {data, error} = await userApi.signUp(captchaResult, params);
    isRegistering = false;
    if (data) {
      if (data.status === '1') {
        errorEmailMsg = LG(lang.TheAccountHasBeen);
        return;
      }

      const props = {
        params,
        type: 'mail-registration-code',
        context: data,
        isHideHeader: true,
        async callback(mailCode, callback, context) {
          const code = context.code;
          const email = context.email;
          const data = await confirmLoginSure(code, mailCode, email, referralCode);
          callback(data);
        },
      };
      if (modalUI && !envUtil.isMobile) {
        mc.send('modal-register', 'launchSafetyVerification', props);
      } else {
        drawerUtil.launchSafetyVerificationPage(LG, props);
      }
    }
    if (error) {
      mc.send('toast', 'error', error);
    }
  }

  async function quickRegister(params, captchaResult) {
    const {data, error} = await transferApi.quickRegister(captchaResult, {...params, connectIdentifier: sourceNoKycUserCode});
    isRegistering = false;
    if (data) {
      if (data.status === '1') {
        errorEmailMsg = LG(lang.TheAccountHasBeen);
        return;
      }
      sd.track('complete_register_from_tr', {
        is_succeed: true,
        fail_reason: '',
        platform: 'web',
        source_page: 'tr_register_success_page',
      });
      navAfterRegisterSuccess();
    }
    if (error) {
      sd.track('complete_register_from_tr', {
        is_succeed: false,
        fail_reason: error.message,
        platform: 'web',
        source_page: 'tr_register_success_page',
      });
      mc.send('toast', 'error', error);
    }
  }

  async function connectRegister(params, captchaResult) {
    const {data, error} = await userApi.crossSiteSignUp(captchaResult, {...params, connectIdentifier: sourceKycUserCode});
    isRegistering = false;
    if (data) {
      if (data.status === '1') {
        errorEmailMsg = LG(lang.TheAccountHasBeen);
        return;
      }
      if (sourceSite === ConnectSourceSite.Turkey) {
        sd.track('complete_register_from_tr', {
          is_succeed: true,
          fail_reason: '',
          platform: 'web',
          source_page: 'create_phemex_account_with_phemex_tr',
        });
      }
      isConnectDone = true;
      return;
    }
    if (error) {
      if (sourceSite === ConnectSourceSite.Turkey) {
        sd.track('complete_register_from_tr', {
          is_succeed: false,
          fail_reason: error.message,
          platform: 'web',
          source_page: 'create_phemex_account_with_phemex_tr',
        });
      }
      mc.send('toast', 'error', error);
    }
  }

  function getRedirectUrl(fixLink, action) {
    const target = getRedirectTarget();
    if (target && isValidRedirectTarget(target)) {
      clearRedirectTarget();
      return target;
    }

    return fixLink(PATHS.ACCOUNT_OVERVIEW.pathname);
  }

  async function confirmLoginSure(code, mailCode, email, referralCode) {
    sd.track('verify', {verify_type: 'email_verify', business_type: 'register'});
    ga.event('user.register', 'verify_submit', 'verify.submit_total');
    const {data, error} = await userApi.confirmRegister({code, mailCode, email});
    if (data) {
      sd.track('complete_register', {
        is_succeed: true,
        verify_type: 'email_verify',
        referral_code: referralCode,
        channel,
      });
      ga.event('user.register', 'verify_submit', 'verify.submit_success');
      await pixelTrackRegister('email');
      localStorage.setItem('close-receive-member', 'N');
      localStorage.setItem('base-kyc-status', '1');
      mc.send('toast', 'info', {message: LG(lang.Registered)});
      await ga.bindGoogleClientId();
      // delay load page for sd
      setTimeout(() => {
        ga.event('user.welcome', 'open_welcome_page', 'after_register');
        window.location.assign(getRedirectUrl($fixLink$, query.action));
      }, 200);
      return {code: 0, msg: ''};
    }
    if (error) {
      sd.track('complete_register', {
        is_succeed: false,
        fail_reason: error.message,
        verify_type: 'email_verify',
        referral_code: referralCode,
      });
      mc.send('toast', 'error', error);
      return {code: error.code, msg: error.message};
    }
  }

  function handleEnterKey(e) {
    if (e.keyCode === 13) {
      ga.event('user.register', 'btn_click', 'form.continue');
      handleButtonContinueClick();
    }
  }

  function handleFocusFun(type) {
    return function () {
      if (type === 'password_input_box') {
        isShowPasswordTips = true;
        isPasswordTips = false;
      }
      sd.track('input_box_focus', {input_box_type: type, business_type: 'register', ui_position: 'register_page'});
    };
  }

  function handleBlurFun(type) {
    return function () {
      errorEmailMsg = validateEmail(emailAccount, LG, lang);
      if (type === 'password_input_box') {
        errorPasswordMsg = validatePassword(password);
        isPasswordTips = true;
      }
      if (!errorPasswordMsg) {
        isShowPasswordTips = false;
      }
      sd.track('input_box_unfocus', {input_box_type: type, business_type: 'register', ui_position: 'register_page'});
    };
  }

  function validatePassword(value) {
    if (value === '') {
      return LG(lang.NoPasswordError);
    }
    if (value && (!PATTERNS.PASSWORD.test(value) || !PATTERNS.CHARACTER_ASC.test(value))) {
      return LG(lang.PasswordError);
    }
    return null;
  }
</script>

<style>
  .warp {
    border-radius: 12px;
    width: 480px;
    background: #fff;
  }
  .form {
    margin: auto !important;
  }
  .bottom {
    margin-top: 20px;
    line-height: 18px;
    font-size: 12px;
  }
  .blue {
    color: var(--blue);
  }
  .sr {
    color: #6c6e74;
    cursor: pointer;
  }
  .a1 {
    padding-left: 0;
    line-height: 22px;
    font-size: 12px;
    cursor: pointer;
  }
  .a1:hover {
    padding-left: 0;
    opacity: 0.8;
  }
  .title {
    color: #1d2029;
  }
  .m-title {
    display: none;
  }
  .warp :global(.home-register-invite-code input[disabled]) {
    color: var(--T2);
    opacity: 1;
  }
  .enjoy-phemex-tr:hover .enjoy {
    display: block;
  }
  .enjoy {
    display: none;
  }
  .home-register-email,
  .home-register-password {
    margin-top: 4px;
  }
  .btn {
    margin-top: 20px;
  }
  .btn :global(button.primary) {
    background: var(--BTN-BG);
    color: var(--BTN-COLOR);
    border-color: var(--BTN-BG);
  }

  @media screen and (min-width: 640px) {
    .gift-con {
      position: absolute;
      top: -18px;
      right: 0;
    }
    .gift-text {
      box-sizing: border-box;
      padding: 0 36px 0 6px;
      height: 18px;
      font-weight: 400;
      background: #f66;
    }
    .gift-img {
      position: absolute;
      top: -13px;
      right: -8px;
      z-index: 10;
      width: 40px;
      height: 40px;
      animation: shake 2s infinite;
    }
    @keyframes shake {
      0%,
      65% {
        transform: rotate(0deg);
      }
      70% {
        transform: rotate(6deg);
      }
      75% {
        transform: rotate(-6deg);
      }
      80% {
        transform: rotate(6deg);
      }
      85% {
        transform: rotate(-6deg);
      }
      90% {
        transform: rotate(6deg);
      }
      95% {
        transform: rotate(-6deg);
      }
      100% {
        transform: rotate(0deg);
      }
    }
  }
  @media screen and (max-width: 640px) {
    .warp {
      box-sizing: border-box;
      padding: 32px 20px;
      width: auto;
    }
    .title,
    .gift-con {
      display: none;
    }
    .m-title {
      display: flex;
    }
  }
  .modal-ui .title,
  .modal-ui .title2 {
    text-align: left;
  }
  .modal-ui .title2 {
    color: #9fa3ab;
  }
  .modal-ipt-title {
    margin-left: 4px;
    line-height: 16px;
    font-size: 12px;
  }
  .modal-ui .home-register-email,
  .modal-ui .home-register-password {
    margin-top: 10px;
  }
  .account-title {
    line-height: 16px;
    font-size: 12px;
  }

  .form :global(.item) {
    border: 1px solid #dcdfe5;
  }
  .form .rotate-bottom {
    transform: rotate(180deg);
  }
  .warp :global(.br48) {
    border-radius: 48px;
  }
  .warp .c-grey {
    color: #6c6e74;
  }
</style>

<svelte:window bind:innerWidth />
{#if isConnectDone}
  <RegisterSuccess />
{:else}
  <div class="warp" class:modal-ui={modalUI}>
    {#if !sourceKycUserCode}
      <div class="title f32 fw3 T1 lh48">{LG($kolInfo$ ? lang.YouAreInvited : registerLang.WelcomeToPhemex)}</div>
    {/if}
    <div class="m-title lh48 f24 fw3 T1">
      <div>{LG($kolInfo$ ? lang.YouAreInvited : registerLang.CreateAccount)}</div>
    </div>
    {#if sourceKycUserCode}
      {#if sourceSite === ConnectSourceSite.Turkey}
        <div class="title2 mt32 lh18 f14 T3 tl">{LG(lang.ConnectTrRegisterDesc)}</div>
      {/if}
    {/if}
    {#if sourceKycUserCode}
      <div class="pv16">
        <ConnectAccountIndicator />
      </div>
    {/if}
    <div class="form pr">
      <div class="modal-ipt-title c-grey mt24">{LG(lang.Email)}</div>
      <div class="home-register-email">
        <EmailInput
          class="br48"
          haveIcon={true}
          disabled={sourceKycUserCode}
          autocomplete="new-password"
          isError={!!errorEmailMsg}
          placeholder={LG(lang.Email)}
          bind:value={email}
          {modalUI}
          on:keypress={handleEnterKey}
          on:focus={handleFocusFun('email_input_box')}
          on:blur={handleBlurFun('email_input_box')}
        />
      </div>
      {#if errorEmailMsg}
        <div class="sell mt10">{errorEmailMsg}</div>
      {/if}
      <div class="modal-ipt-title c-grey mt20">{LG(lang.Password)}</div>
      <div class="home-register-password">
        <PasswordInput
          class="br48"
          showIcon={false}
          autocomplete="new-password"
          isError={!!errorPasswordMsg}
          placeholder={LG(lang.Password)}
          disabled={isRegistering}
          bind:value={password}
          {isPasswordTips}
          {modalUI}
          {isShowPasswordTips}
          {innerWidth}
          placement={innerWidth > 640 ? 'left' : 'right'}
          on:keypress={handleEnterKey}
          on:focus={handleFocusFun('password_input_box')}
          on:blur={handleBlurFun('password_input_box')}
        />
      </div>
      <div class="code mt20">
        <div class="df aic lh16 f12 cp c-grey" on:click={handleSeeCodeClick}>
          {LG(lang.InvitationCode)}<img width="12" height="12" class="ml4" class:rotate-bottom={!isSeeCode} src={optionalArrowIcon} />
        </div>
        {#if isSeeCode}
          <EmailInput
            class="mt16 home-register-invite-code br48"
            hasCodeIcon={modalUI}
            haveIcon
            maxLength="32"
            disabled={hasReferral}
            placeholder={LG(lang.InvitationCode)}
            bind:value={referralCode}
            on:keypress={handleEnterKey}
          />
        {/if}
      </div>
    </div>
    <div class="bottom df c-grey">
      <i class="iconfont f14 cp {isTerm ? 'T1' : 'T4'}" on:click={() => (isTerm = !isTerm)}>{@html isTerm ? '&#xe61f;' : '&#xe6d3;'}</i>
      <div class="ml8 f12">{@html terms}</div>
    </div>
    <div class="btn ga pr" data-gac="user.register" data-gaa="btn_click" data-gal="form.continue">
      <Button
        class="home-register-button pr f16 fw3 br48"
        type="primary"
        newRadius
        minWidth="100%"
        on:click={handleButtonContinueClick}
        loading={isRegistering}
        disabled={isInitializing || !isTerm || (!email && !password)}
      >
        {LG(isInitializing ? lang.Initializing : lang.Continue)}
      </Button>
    </div>
    {#if registerUIVersion === 3 && !sourceNoKycUserCode}
      <div class="login-account mt20">
        <label class="c-grey account-title">{LG(lang.AlreadyHave)}</label>
        <span class="sr f12 lh16">
          <a class="a1 fw3 T1 noa" href={loginUrl} target="_self">{LG(lang.LogIn)}</a>
        </span>
      </div>
    {/if}
    {#if (!modalUI || modal == 1) && registerUIVersion !== 1}
      <SplitLine text={LG(lang.OrContinueWith)} />
      <ThirdPartyLogin target={getRedirectTarget()} {referralCode} entrance="Register" />
    {/if}
    {#if sourceKycUserCode}
      <ConnectAccountTips />
    {/if}
  </div>
{/if}
